import L from "leaflet";
import type { LatLngTuple } from "../core/options";
import { midpoint } from "../geo/geodesy";
import { format } from "../i18n/labels";
import type { ToolHost } from "./host";
import { insertPoint, removePoint, replacePoint } from "./LineOps";

export type VertexKind = "start" | "end" | "middle" | "single";

function handleIcon(kind: VertexKind | "mid", touch: boolean, selected: boolean): L.DivIcon {
  const visible = kind === "mid" ? 12 : 16;
  const hit = touch ? 44 : visible + 8;

  return L.divIcon({
    className: "fxmap-vertex-hit fxmap-cursor-grab",
    html: `<span class="fxmap-vertex fxmap-vertex-${kind}${selected ? " fxmap-vertex-selected" : ""}" data-fxmap="draw-vertex" style="width:${visible}px;height:${visible}px"></span>`,
    iconSize: [hit, hit],
    iconAnchor: [hit / 2, hit / 2]
  });
}

export function kindOf(index: number, count: number, closed: boolean): VertexKind {
  if (count === 1) return "single";
  if (closed) return index === 0 ? "start" : "middle";
  if (index === 0) return "start";
  if (index === count - 1) return "end";

  return "middle";
}

export class VertexHandles {
  private readonly host: ToolHost;
  private readonly closed: boolean;
  private readonly group: L.LayerGroup;
  private enabled = true;
  private dragging = false;

  constructor(host: ToolHost, closed: boolean) {
    this.host = host;
    this.closed = closed;
    this.group = L.layerGroup([], { pane: host.pane }).addTo(host.host.map);
  }

  setEnabled(on: boolean): void {
    this.enabled = on;
    this.render(this.host.points());
  }

  isDragging(): boolean {
    return this.dragging;
  }

  render(points: LatLngTuple[]): void {
    this.group.clearLayers();

    if (!this.enabled) return;

    const labels = this.host.host.labels;
    const touch = this.host.host.touch;
    const selected = this.host.selectedVertex();

    points.forEach((point, index) => {
      const marker = L.marker(point, {
        icon: handleIcon(kindOf(index, points.length, this.closed), touch, selected === index),
        draggable: true,
        keyboard: true,
        pane: this.host.pane,
        zIndexOffset: 1000,
        autoPan: true
      });

      marker.bindTooltip(format(labels.vertexTooltip, { index: index + 1 }), { direction: "top", opacity: 0.9 });

      marker.on("dragstart", () => {
        this.dragging = true;
        marker.closeTooltip();
      });

      marker.on("drag", event => {
        const position = (event.target as L.Marker).getLatLng();
        const snapped = this.snap([position.lat, position.lng], (event as unknown as { originalEvent?: MouseEvent }).originalEvent?.shiftKey ?? false, index);

        this.host.preview(replacePoint(this.host.points(), index, snapped));
      });

      marker.on("dragend", () => {
        const position = marker.getLatLng();
        const snapped = this.snap([position.lat, position.lng], false, index);

        this.dragging = false;
        this.host.commit(replacePoint(this.host.points(), index, snapped));
      });

      marker.on("contextmenu", event => {
        L.DomEvent.stop(event);
        this.remove(index);
      });

      marker.on("click", event => {
        L.DomEvent.stopPropagation(event);
        this.host.selectVertex(this.host.selectedVertex() === index ? null : index);
        this.render(this.host.points());
      });
      marker.on("dblclick", event => L.DomEvent.stop(event));

      marker.on("add", () => {
        const element = marker.getElement();

        if (element === undefined) return;

        L.DomEvent.on(element, "keydown", event => {
          const key = (event as KeyboardEvent).key;

          if (key === "Delete" || key === "Backspace") {
            L.DomEvent.stop(event);
            this.remove(index);
          }
        });
      });

      marker.addTo(this.group);
    });

    const segmentCount = this.closed ? points.length : points.length - 1;

    if (points.length < 2) return;

    for (let index = 0; index < segmentCount; index++) {
      const from = points[index];
      const to = points[(index + 1) % points.length];
      const middle = midpoint(from, to);
      const marker = L.marker(middle, {
        icon: handleIcon("mid", touch, false),
        draggable: true,
        keyboard: false,
        pane: this.host.pane,
        zIndexOffset: 900,
        opacity: 0.9
      });

      marker.bindTooltip(labels.midpointTooltip, { direction: "top", opacity: 0.9 });

      marker.on("dragstart", () => {
        this.dragging = true;
        marker.closeTooltip();
      });

      marker.on("drag", event => {
        const position = (event.target as L.Marker).getLatLng();
        const snapped = this.snap([position.lat, position.lng], (event as unknown as { originalEvent?: MouseEvent }).originalEvent?.shiftKey ?? false, -1);

        this.host.preview(insertPoint(this.host.points(), index + 1, snapped));
      });

      marker.on("dragend", () => {
        const position = marker.getLatLng();
        const snapped = this.snap([position.lat, position.lng], false, -1);

        this.dragging = false;
        this.host.commit(insertPoint(this.host.points(), index + 1, snapped));
      });

      marker.on("click", event => {
        L.DomEvent.stop(event);
        this.host.commit(insertPoint(this.host.points(), index + 1, middle));
      });

      marker.on("dblclick", event => L.DomEvent.stop(event));
      marker.addTo(this.group);
    }
  }

  removeSelected(): void {
    const selected = this.host.selectedVertex();

    if (selected === null) return;

    this.host.selectVertex(null);
    this.remove(selected);
  }

  clear(): void {
    this.group.clearLayers();
  }

  dispose(): void {
    this.group.clearLayers();
    this.group.remove();
  }

  private remove(index: number): void {
    if (!this.enabled) return;

    if (this.host.selectedVertex() === index) this.host.selectVertex(null);

    this.host.commit(removePoint(this.host.points(), index));
  }

  private snap(point: LatLngTuple, shiftHeld: boolean, ownIndex: number): LatLngTuple {
    const own = this.host.points();
    const result = this.host.snapper.snap(point, shiftHeld, candidate => candidate.id === -1 && ownIndex >= 0 && candidate.points.some(a => a === own[ownIndex]));

    return result.point;
  }
}
